import { Box, Button, Popover, Text, UnstyledButton } from "@mantine/core";
import { DatePicker, type DatesRangeValue } from "@mantine/dates";
import { PlusIcon, XIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import classes from "./DateRangeChip.module.css";

export type DateRangeValue = [string | null, string | null];

export type DateRangePreset = {
  key: string;
  label: string;
  from: string;
  to: string;
};

type Props = {
  /** Chip label, e.g. «Батлагдсан огноо». */
  label: string;
  /** Applied range as YYYY-MM-DD. */
  value: DateRangeValue;
  onChange: (range: DateRangeValue) => void;
  /** Override or disable default presets. */
  presets?: DateRangePreset[] | false;
  clearable?: boolean;
  maxDate?: Date | string;
  /** Initial month when opening with no value. */
  defaultMonth?: Date | string;
  size?: "default" | "compact";
  className?: string;
};

function toYmd(v: string | Date | null | undefined): string | null {
  if (!v) return null;
  return dayjs(v).format("YYYY-MM-DD");
}

function quarterBounds(d = dayjs()) {
  const q = Math.floor(d.month() / 3);
  const start = d.month(q * 3).startOf("month");
  const end = start.add(2, "month").endOf("month");
  return [start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD")] as const;
}

export function formatDateRangeChipLabel(from: string | null, to: string | null): string | null {
  if (!from && !to) return null;
  const d1 = from ? dayjs(from) : null;
  const d2 = to ? dayjs(to) : null;
  if (d1 && d2 && d1.isSame(d2, "day")) {
    return d1.format("YYYY-MM-DD");
  }
  return `${d1 ? d1.format("YYYY-MM-DD") : "…"} - ${d2 ? d2.format("YYYY-MM-DD") : "…"}`;
}

function monthAnchor(from: string | null, defaultMonth?: Date | string): Date {
  if (from) return dayjs(from).startOf("month").toDate();
  if (defaultMonth) return dayjs(defaultMonth).startOf("month").toDate();
  return dayjs().subtract(1, "month").startOf("month").toDate();
}

export function DateRangeChip({
  label,
  value,
  onChange,
  presets,
  clearable = true,
  maxDate,
  defaultMonth,
  size = "default",
  className,
}: Props) {
  const { t } = useTranslation("common");
  const [opened, setOpened] = useState(false);
  const [draft, setDraft] = useState<DatesRangeValue>([null, null]);
  const [calendarDate, setCalendarDate] = useState<Date>(() => monthAnchor(value[0], defaultMonth));

  const defaultPresets = useMemo<DateRangePreset[]>(() => {
    const now = dayjs();
    const [qStart, qEnd] = quarterBounds(now);
    return [
      {
        key: "thisMonth",
        label: t("dateRange.thisMonth"),
        from: now.startOf("month").format("YYYY-MM-DD"),
        to: now.endOf("month").format("YYYY-MM-DD"),
      },
      {
        key: "prevMonth",
        label: t("dateRange.prevMonth"),
        from: now.subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
        to: now.subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
      },
      {
        key: "last30",
        label: t("dateRange.last30"),
        from: now.subtract(29, "day").format("YYYY-MM-DD"),
        to: now.format("YYYY-MM-DD"),
      },
      {
        key: "quarter",
        label: t("dateRange.quarter"),
        from: qStart,
        to: qEnd,
      },
      {
        key: "year",
        label: t("dateRange.year"),
        from: now.startOf("year").format("YYYY-MM-DD"),
        to: now.endOf("year").format("YYYY-MM-DD"),
      },
    ];
  }, [t]);

  const presetList = presets === false ? [] : presets ?? defaultPresets;
  const rangeLabel = formatDateRangeChipLabel(value[0], value[1]);
  const draftComplete = !!(draft?.[0] && draft?.[1]);
  const max = maxDate ? dayjs(maxDate).toDate() : undefined;

  const openPopover = (next: boolean) => {
    if (next) {
      setDraft([value[0], value[1]]);
      setCalendarDate(monthAnchor(value[0], defaultMonth));
    } else {
      setDraft([null, null]);
    }
    setOpened(next);
  };

  const commit = (from: string, to: string) => {
    onChange([from, to]);
    setOpened(false);
    setDraft([null, null]);
  };

  const clear = () => {
    onChange([null, null]);
    setDraft([null, null]);
    setOpened(false);
  };

  const applyDraft = (range?: DatesRangeValue) => {
    const r = range ?? draft;
    const start = toYmd(r?.[0] ?? null);
    const end = toYmd(r?.[1] ?? null);
    if (!start || !end) return;
    commit(start, end);
  };

  const applyPreset = (from: string, to: string) => {
    setCalendarDate(dayjs(from).startOf("month").toDate());
    commit(from, to);
  };

  return (
    <Popover
      opened={opened}
      onChange={openPopover}
      position="bottom-start"
      shadow="md"
      withinPortal
      closeOnClickOutside
    >
      <Popover.Target>
        <Box
          component="button"
          type="button"
          className={`${classes.chip}${rangeLabel ? ` ${classes.chipActive}` : ""}${
            size === "compact" ? ` ${classes.chipCompact}` : ""
          }${className ? ` ${className}` : ""}`}
          aria-expanded={opened}
          aria-haspopup="dialog"
          onClick={() => openPopover(!opened)}
        >
          {rangeLabel ? (
            <>
              <Text span size="sm" fw={500}>
                {rangeLabel}
              </Text>
              {clearable && (
                <XIcon
                  size={12}
                  weight="bold"
                  onClick={(e) => {
                    e.stopPropagation();
                    clear();
                  }}
                />
              )}
            </>
          ) : (
            <>
              <PlusIcon size={12} weight="bold" />
              <Text span size="sm">
                {label}
              </Text>
            </>
          )}
        </Box>
      </Popover.Target>
      <Popover.Dropdown className={classes.popover}>
        <Box className={classes.inner}>
          {presetList.length > 0 && (
            <Box className={classes.presets} role="list">
              {presetList.map((p) => (
                <UnstyledButton
                  key={p.key}
                  className={classes.presetBtn}
                  onClick={() => applyPreset(p.from, p.to)}
                >
                  {p.label}
                </UnstyledButton>
              ))}
            </Box>
          )}
          <Box className={classes.calendar}>
            <DatePicker
              type="range"
              numberOfColumns={2}
              allowSingleDateInRange
              value={draft}
              date={calendarDate}
              onDateChange={(d) => setCalendarDate(dayjs(d).toDate())}
              onChange={(val) => {
                setDraft(val);
                if (val?.[0] && val?.[1]) {
                  applyDraft(val);
                }
              }}
              maxDate={max}
            />
            <Box className={classes.footer}>
              {clearable && (
                <Button variant="subtle" color="gray" size="compact-sm" onClick={clear}>
                  {t("dateRange.clear")}
                </Button>
              )}
              <Button size="compact-sm" disabled={!draftComplete} onClick={() => applyDraft()}>
                {t("dateRange.apply")}
              </Button>
            </Box>
          </Box>
        </Box>
      </Popover.Dropdown>
    </Popover>
  );
}
